// ── Portfolio by State ────────────────────────────────────────────────────
//
// A choropleth of the book by borrower state, on either Commitment or BS Loan
// Balance.
//
// IT READS THE SAME ROWS AS THE PORTFOLIO BY ACCOUNT TABLE ABOVE IT. That is
// deliberate and it is the only reason the two agree. getAccounts carries its
// own commitment and balance columns, and using those would have been less
// code — but they are computed differently, so the map's grand total would
// have quietly disagreed with the table six inches above it. Instead the
// account rows are joined to getAccounts ONLY for the state, and every dollar
// on this map comes from portfolioByAccount, through the same Paid Off filter
// the table applies.
//
// No map library. src/usStates.js holds pre-projected path strings; see its
// header for why.

import { useState, useMemo } from "react";
import { B, RAMP_BLUE, SURFACE, SURFACE_MUTED, HAIRLINE_FIRM, HAIRLINE, INK_MUTED, INK_PRIMARY } from "../../../shared/theme";
import { Card, Td, TdNum, Th } from "../../../shared/ui";
import { fmt, fmtRatio } from "../../../shared/format";
import { US_VIEWBOX, US_STATE_PATHS, US_STATE_NAMES,
         US_SMALL_STATES } from "../../../shared/usStates";

// The blue sequential ramp from shared/theme: pale cyan to cyanDark. It stops
// short of B.sidebar on purpose — see the ramp's own comment.
const RAMP = RAMP_BLUE;
// A state with no borrowers is BLANK — the colour of the card it sits on, with
// a visible outline so the country still has a shape everywhere the book is
// not, which is most of it. Filled states get a white hairline between them
// instead; an empty one needs the edge.
//
// This was painting BLACK. `fill` given an unresolvable var() is invalid at
// computed-value time and falls back to the initial value, which in SVG is
// black — and --color-background-secondary is defined nowhere in this repo.
// Both tokens below carry a literal fallback; nothing inside an <svg> on this
// page uses a bare var().
const EMPTY_FILL   = SURFACE;
const EMPTY_STROKE = HAIRLINE_FIRM;

const METRICS = [
  { id: "commitment", label: "Commitment",      field: "Commitment" },
  { id: "bs",         label: "BS Loan Balance", field: "BSExposure" },
];

// Accounts.state is free text: "MN", "mn", "Minnesota", " Minn. " all occur in
// data that has been typed by hand for years. Everything that cannot be
// resolved to one of the 50 states or DC is counted as unassigned and reported
// as such rather than silently dropped — a state total that is quietly missing
// three borrowers is worse than no map.
const NAME_TO_ABBR = Object.entries(US_STATE_NAMES)
  .reduce((m, [ab, name]) => { m[name.toLowerCase()] = ab; return m; }, {});
const normState = raw => {
  if (!raw) return null;
  const s = String(raw).trim().replace(/\.$/, "");
  if (!s) return null;
  const up = s.toUpperCase();
  if (US_STATE_PATHS[up]) return up;
  return NAME_TO_ABBR[s.toLowerCase()] || null;
};

const num = v => parseFloat(v || 0) || 0;
const shortUSD = v => {
  const a = Math.abs(v);
  const t = x => String(Math.abs(x) >= 100 ? Math.round(x) : Math.round(x * 10) / 10);
  return a >= 1e9 ? "$" + t(v / 1e9) + "B"
       : a >= 1e6 ? "$" + t(v / 1e6) + "M"
       : a >= 1e3 ? "$" + t(v / 1e3) + "K"
       : "$" + Math.round(v);
};

// Quantile breaks over the states that actually carry something. Linear bins
// would put one $60mm state at the dark end and flatten every other state into
// the palest bucket, which is a picture of the maximum, not of the book.
const makeScale = values => {
  const v = values.filter(x => x > 0).sort((a, b) => a - b);
  if (!v.length) return { breaks: [], bucket: () => -1, max: 0 };
  const breaks = [];
  for (let i = 1; i < RAMP.length; i++) breaks.push(v[Math.floor(i * v.length / RAMP.length)]);
  return {
    breaks, max: v[v.length - 1], min: v[0],
    bucket: x => {
      if (!(x > 0)) return -1;
      let i = 0;
      while (i < breaks.length && x >= breaks[i]) i++;
      return i;
    },
  };
};

// Which borrowers the map covers, by the account's portfolio status.
const STATUSES = [
  { id: "all",        label: "All",         test: () => true },
  { id: "accrual",    label: "Accrual",     test: r => r.accountstatus === "Accrual" },
  { id: "nonaccrual", label: "Non Accrual", test: r => r.accountstatus === "Non Accrual" },
];

export const PortfolioHeatMap = ({ rows: allRows = [], accounts = [] }) => {
  const [metric, setMetric] = useState("commitment");
  const [status, setStatus] = useState("all");
  const st = STATUSES.find(x => x.id === status) || STATUSES[0];
  const rows = useMemo(() => allRows.filter(st.test), [allRows, st]);
  const [hover, setHover] = useState(null);
  const m = METRICS.find(x => x.id === metric) || METRICS[0];

  const { byState, unassigned, scale, total } = useMemo(() => {
    const stateOf = {}, industryOf = {};
    accounts.forEach(a => {
      stateOf[String(a.id)] = normState(a.state);
      const ind = a.industryname === null || a.industryname === undefined
                  || String(a.industryname).trim() === "" ? null : String(a.industryname).trim();
      industryOf[String(a.id)] = ind;
    });

    const byState = {}, unassigned = { n: 0, commitment: 0, bs: 0, names: [] };
    let total = 0;
    rows.forEach(r => {
      const st = stateOf[String(r.id)];
      const ind = industryOf[String(r.id)];
      const c = num(r.Commitment), b = num(r.BSExposure);
      total += m.id === "commitment" ? c : b;
      if (!st) {
        unassigned.n++; unassigned.commitment += c; unassigned.bs += b;
        if (unassigned.names.length < 12) unassigned.names.push(r.companyname);
        return;
      }
      const e = byState[st] = byState[st] || { st, n: 0, commitment: 0, bs: 0,
                                               accounts: [], industries: {} };
      e.n++; e.commitment += c; e.bs += b;
      e.accounts.push({ id: r.id, name: r.companyname, commitment: c, bs: b, industry: ind });

      // Concentration is the reason to look at a map like this, and it is not
      // only geographic — four borrowers in one state doing the same thing is
      // a different risk from four doing different things. Industry is
      // aggregated in dollars as well as counts, because two small printers
      // and one large one are not the same picture.
      const ik = ind || "~none";
      const g = e.industries[ik] = e.industries[ik] || { name: ind, n: 0, commitment: 0, bs: 0 };
      g.n++; g.commitment += c; g.bs += b;
    });

    const val = e => m.id === "commitment" ? e.commitment : e.bs;
    Object.values(byState).forEach(e => {
      e.accounts.sort((x, y) => val(y) - val(x));
      e.industryList = Object.values(e.industries).sort((x, y) => val(y) - val(x));
    });

    return { byState, unassigned, total, scale: makeScale(Object.values(byState).map(val)) };
  }, [rows, accounts, m.id]);

  const valOf = st => { const e = byState[st]; return e ? (m.id === "commitment" ? e.commitment : e.bs) : 0; };
  const stepOf = st => { const i = scale.bucket(valOf(st)); return i < 0 ? null : RAMP[i]; };
  const fillOf = st => { const s = stepOf(st); return s ? s.fill : EMPTY_FILL; };


  const ranked = Object.values(byState)
    .map(e => ({ ...e, value: m.id === "commitment" ? e.commitment : e.bs }))
    .filter(e => e.value > 0)
    .sort((a, b) => b.value - a.value);

  const shown = hover && byState[hover] ? byState[hover] : null;
  // Denominator for the industry shares: the state's own total on the
  // metric being displayed, so the bars read as "share of this state".
  const shownValue = shown ? (m.id === "commitment" ? shown.commitment : shown.bs) : 0;

  const tabBtn = active => ({
    padding: "5px 12px", fontSize: 12, fontWeight: active ? 700 : 500, cursor: "pointer",
    borderRadius: 6, fontFamily: "inherit",
    border: "1px solid " + (active ? B.cyan : HAIRLINE_FIRM),
    background: active ? B.cyanLight : SURFACE,
    color: active ? B.cyanDark : INK_PRIMARY,
  });

  const cardTitle = { fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase",
                      letterSpacing:"0.06em", marginBottom:10, paddingBottom:6,
                      borderBottom:"1.5px solid "+B.cyanLight };

  const bigStates = Object.keys(US_STATE_PATHS).filter(s => !US_SMALL_STATES.includes(s));

  const stateProps = st => ({
    onMouseEnter: () => setHover(st),
    onMouseLeave: () => setHover(h => (h === st ? null : h)),
    style: { transition: "fill 120ms" },
  });

  return (
    // The same 16px either side as every other dashboard card (vertical margins
    // collapse, so this is the gap, not twice it).
    <Card style={{ padding:16, marginTop:16, marginBottom:16 }}>
      <div style={cardTitle}>Portfolio by State</div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:10, alignItems:"center",
                    justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {METRICS.map(x => (
            <button key={x.id} type="button" style={tabBtn(x.id === metric)}
                    onClick={() => setMetric(x.id)}>{x.label}</button>
          ))}
          {/* Which loans: the whole active book, or one status. */}
          <span style={{ width:1, height:20, background:HAIRLINE_FIRM, margin:"0 6px" }} />
          {STATUSES.map(x => (
            <button key={x.id} type="button" style={tabBtn(x.id === status)}
                    onClick={() => setStatus(x.id)}>{x.label}</button>
          ))}
        </div>

        {/* Legend carries the actual break values. A ramp with no numbers on it
            tells you there is more somewhere without saying how much more. */}
        <div style={{ display:"flex", alignItems:"center", gap:8, fontSize:11,
                      color:INK_MUTED }}>
          <span>{shortUSD(scale.min || 0)}</span>
          <span style={{ display:"flex" }}>
            {RAMP.map((c, i) => (
              <span key={c.fill} title={
                      i === 0 ? `under ${shortUSD(scale.breaks[0] || 0)}`
                    : i === RAMP.length - 1 ? `${shortUSD(scale.breaks[i-1] || 0)} and up`
                    : `${shortUSD(scale.breaks[i-1] || 0)} – ${shortUSD(scale.breaks[i] || 0)}`}
                    style={{ width:26, height:11, background:c.fill,
                             borderTopLeftRadius: i === 0 ? 3 : 0,
                             borderBottomLeftRadius: i === 0 ? 3 : 0,
                             borderTopRightRadius: i === RAMP.length-1 ? 3 : 0,
                             borderBottomRightRadius: i === RAMP.length-1 ? 3 : 0 }} />
            ))}
          </span>
          <span>{shortUSD(scale.max || 0)}</span>
          <span style={{ marginLeft:4, display:"inline-flex", alignItems:"center", gap:5 }}>
            <span style={{ width:11, height:11, background:SURFACE_MUTED, borderRadius:3,
                           border:"1px solid "+HAIRLINE }} />
            none
          </span>
        </div>
      </div>

      {/* ── one row: numbers, then map, then readout ──────────────────────
          The table used to sit UNDER the map at full width, which made the
          card tall enough to push everything below it off the first screen.
          Beside the map it costs no extra height — the row is as tall as its
          tallest child — and the map gives up the width it was only using to
          be big. A choropleth does not get more readable past a certain size;
          it just gets larger.

          Numbers first because they are what gets read. The map is the index
          into them, not the other way round. */}
      <div style={{ display:"flex", flexWrap:"wrap", gap:16, alignItems:"flex-start" }}>
        {/* Widest basis in the row, so when space is tight the table keeps its
            columns and the map surrenders first. overflowX is the escape hatch
            below the wrap point rather than letting six columns crush. */}
        {/* Takes the remainder — grow 1 against the map's 0. Its basis is small
            so the map claims its half first; below the wrap point the whole
            column drops under the map rather than crushing six columns. */}
        <div style={{ flex:"1 1 340px", minWidth:300, overflowX:"auto" }}>
        {/* ── ranked table ───────────────────────────────────────────────────
            The map answers "where"; this answers "how much, exactly". A
            choropleth alone cannot be read to the dollar and should not pretend
            otherwise. */}
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              <Th>State</Th>
              <Th style={{ textAlign:"right" }}>Accounts</Th>
              <Th>Top Industry</Th>
              <Th style={{ textAlign:"right" }}>Commitment</Th>
              <Th style={{ textAlign:"right" }}>BS Loan Balance</Th>
              <Th style={{ textAlign:"right" }}>% of {m.label}</Th>
            </tr>
          </thead>
          <tbody>
            {ranked.map(e => (
              <tr key={e.st}
                  onMouseEnter={() => setHover(e.st)}
                  onMouseLeave={() => setHover(h => (h === e.st ? null : h))}
                  style={{ background: hover === e.st ? SURFACE_MUTED : undefined }}>
                {/* Capped rather than shortened to the abbreviation. 150px fits
                    every state name but "District of Columbia", so the column
                    stops sizing itself to its longest row while still reading
                    as a name; the title carries the full text for the one that
                    clips. */}
                <Td style={{ maxWidth:150 }} title={US_STATE_NAMES[e.st]}>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:8,
                                 maxWidth:"100%" }}>
                    <span style={{ width:11, height:11, borderRadius:3, flexShrink:0,
                                   background: RAMP[Math.max(scale.bucket(e.value), 0)].fill }} />
                    <span style={{ overflow:"hidden", textOverflow:"ellipsis",
                                   whiteSpace:"nowrap" }}>{US_STATE_NAMES[e.st]}</span>
                  </span>
                </Td>
                <TdNum>{e.n}</TdNum>
                <Td style={{ color:INK_MUTED, fontSize:12, maxWidth:110, overflow:"hidden",
                             textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                    title={(e.industryList || []).map(g =>
                      `${g.name || "No industry on record"} — ${g.n}, `
                      + fmt(m.id === "commitment" ? g.commitment : g.bs)).join("\n")}>
                  {(() => {
                    const list = e.industryList || [];
                    if (!list.length) return "—";
                    const top = list[0];
                    const name = top.name || "No industry on record";
                    return list.length === 1 ? name : `${name} +${list.length - 1}`;
                  })()}
                </Td>
                <TdNum>{fmt(e.commitment)}</TdNum>
                <TdNum>{fmt(e.bs)}</TdNum>
                <TdNum>{total ? ((e.value / total) * 100).toFixed(1) + "%" : "—"}</TdNum>
              </tr>
            ))}
            {unassigned.n > 0 && (
              <tr style={{ color:INK_MUTED }}>
                <Td><span style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                  <span style={{ width:11, height:11, borderRadius:3, flexShrink:0,
                                 background:EMPTY_FILL, border:"1px solid "+HAIRLINE }} />
                  No state on record
                </span></Td>
                <TdNum>{unassigned.n}</TdNum>
                <Td>—</Td>
                <TdNum>{fmt(unassigned.commitment)}</TdNum>
                <TdNum>{fmt(unassigned.bs)}</TdNum>
                <TdNum>{total ? (((m.id === "commitment" ? unassigned.commitment : unassigned.bs) / total) * 100).toFixed(1) + "%" : "—"}</TdNum>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr style={{ background:B.cyanLight, borderTop:"2px solid "+B.cyan }}>
              <td style={{ padding:"10px 12px", fontSize:13, fontWeight:700, color:B.cyanDark }}>
                {ranked.length} state{ranked.length === 1 ? "" : "s"}
              </td>
              <td style={{ textAlign:"right", padding:"10px 12px", fontSize:13, fontWeight:700,
                           color:B.cyanDark, fontVariantNumeric:"tabular-nums" }}>
                {ranked.reduce((s, e) => s + e.n, 0) + unassigned.n}
              </td>
              {/* Distinct industries across the whole book, not a sum of the
                  column above it — the same industry in three states is one
                  industry, and a total that added them would overstate the
                  diversification this column exists to show. */}
              <td style={{ padding:"10px 12px", fontSize:12, fontWeight:600, color:B.cyanDark }}>
                {(() => {
                  const s = new Set();
                  ranked.forEach(e => (e.industryList || []).forEach(g => s.add(g.name || "~none")));
                  return s.size ? `${s.size} distinct` : "—";
                })()}
              </td>
              <td style={{ textAlign:"right", padding:"10px 12px", fontSize:13, fontWeight:700,
                           color:B.cyanDark, fontVariantNumeric:"tabular-nums" }}>
                {fmt(ranked.reduce((s, e) => s + e.commitment, 0) + unassigned.commitment)}
              </td>
              <td style={{ textAlign:"right", padding:"10px 12px", fontSize:13, fontWeight:700,
                           color:B.cyanDark, fontVariantNumeric:"tabular-nums" }}>
                {fmt(ranked.reduce((s, e) => s + e.bs, 0) + unassigned.bs)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
        </div>

        {/* A THIRD OF THE CARD, stated as a basis rather than a max. "0 1 35%"
            means it shrinks if it has to but never grows past 35%, so the map
            holds the same proportion on a laptop and on a wide monitor instead
            of quietly eating the leftover width. The table is the one that
            grows. The 320px floor is the point below which the small-state
            shapes stop being distinguishable — past that the column wraps
            instead of carrying on shrinking. */}
        <div style={{ flex:"0 1 35%", minWidth:320, position:"relative" }}>
          <svg viewBox={US_VIEWBOX} role="img"
               aria-label={`United States choropleth of portfolio ${m.label.toLowerCase()} by borrower state`}
               style={{ width:"100%", height:"auto", display:"block" }}>
            {bigStates.map(st => (
              <path key={st} d={US_STATE_PATHS[st]} fill={fillOf(st)}
                    stroke={hover === st ? B.cyanDark
                          : valOf(st) > 0 ? SURFACE : EMPTY_STROKE}
                    strokeWidth={hover === st ? 2.5 : valOf(st) > 0 ? 1 : 0.6}
                    {...stateProps(st)} />
            ))}
            {/* NO ABBREVIATIONS PAINTED ON THE MAP.
                They were the loudest thing on the card — 15px bold over a
                shape whose whole job is to carry a colour — and they said
                nothing the reader could not get by pointing at the state,
                which names it in full in the readout. Shading answers "where
                is the money"; the readout answers "which state is that".
                The nine small states keep their chips: those are tiles, not
                shapes, so the abbreviation IS the state rather than a label
                sitting on top of one. */}
          </svg>
        </div>

        {/* minWidth:0 and maxWidth are not belt and braces — without them
            this column is not fixed at all. flex-basis 226px sets the
            PREFERRED width; min-width defaults to auto, so the column silently
            grows to its widest unbreakable child. One long industry name on a
            nowrap line was pushing it to 336px and shrinking the map by 110px,
            which is the resizing that looked like a height problem. */}
        <div style={{ flex:"0 0 226px", minWidth:0, maxWidth:226,
                      display:"flex", flexDirection:"column", gap:10 }}>
          {/* ── the nine that cannot be shaded ───────────────────────────── */}
          <div>
            <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                          color:INK_MUTED, marginBottom:6 }}>
              Small states
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
              {US_SMALL_STATES.map(st => {
                const v = valOf(st);
                const i = scale.bucket(v);
                return (
                  <span key={st} {...stateProps(st)}
                        style={{ display:"inline-flex", alignItems:"center", justifyContent:"center",
                                 width:30, height:24, borderRadius:4, fontSize:10, fontWeight:700,
                                 background: i < 0 ? EMPTY_FILL : RAMP[i].fill,
                                 color: i < 0 ? INK_MUTED : RAMP[i].ink,
                                 border:"1px solid " + (hover === st ? B.cyanDark : HAIRLINE),
                                 opacity: i < 0 ? 0.6 : 1 }}>
                    {st}
                  </span>
                );
              })}
            </div>
          </div>

          {/* ── hover readout ────────────────────────────────────────────── */}
          {/* A FIXED height, not a floor. minHeight held it steady between
              nothing-hovered and a typical state, but a state with more
              industries or longer borrower names still pushed past it — so the
              box, and the map beside it, changed size depending on WHICH state
              you were pointing at. height + overflow makes it one size for
              every state, empty or not: hovering changes what is in the box
              and never how big it is. Content past the box scrolls. */}
          <div style={{ height:360, overflowY:"auto",
                        padding:"10px 12px", borderRadius:8,
                        background:SURFACE_MUTED,
                        border:"1px solid "+HAIRLINE, fontSize:12 }}>
            {shown ? (
              <>
                <div style={{ fontWeight:700, marginBottom:4 }}>{US_STATE_NAMES[shown.st]}</div>
                <div style={{ color:INK_MUTED, marginBottom:6 }}>
                  {shown.n} account{shown.n === 1 ? "" : "s"}
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8 }}>
                  <span style={{ color:INK_MUTED }}>Commitment</span>
                  <span style={{ fontVariantNumeric:"tabular-nums" }}>{fmt(shown.commitment)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8 }}>
                  <span style={{ color:INK_MUTED }}>BS Loan Bal</span>
                  <span style={{ fontVariantNumeric:"tabular-nums" }}>{fmt(shown.bs)}</span>
                </div>
                {/* Industry, ranked by the metric on screen. Geographic
                    concentration and industry concentration are different
                    risks, and a state map that only shows the first one
                    invites the reader to assume the second. */}
                <div style={{ marginTop:8, paddingTop:7,
                              borderTop:"0.5px solid "+HAIRLINE }}>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase",
                                letterSpacing:"0.06em", color:INK_MUTED, marginBottom:5 }}>
                    Industry
                  </div>
                  {(shown.industryList || []).slice(0, 4).map(g => {
                    const v = m.id === "commitment" ? g.commitment : g.bs;
                    const share = shownValue ? v / shownValue : 0;
                    return (
                      <div key={g.name || "~none"}
                           title={`${g.name || "No industry on record"} — ${g.n} account`
                                + `${g.n === 1 ? "" : "s"}\nCommitment ${fmt(g.commitment)}`
                                + `\nBS Loan Balance ${fmt(g.bs)}`}
                           style={{ marginBottom:4 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", gap:6,
                                      alignItems:"baseline" }}>
                          {/* The child half of the same bug: this span already
                              had overflow + ellipsis + nowrap, but it sits in a
                              flex row and carried the same min-width:auto, so it
                              refused to shrink and the ellipsis never engaged. */}
                          <span style={{ overflow:"hidden", textOverflow:"ellipsis",
                                         whiteSpace:"nowrap", minWidth:0, fontSize:11.5,
                                         color: g.name ? undefined : INK_MUTED,
                                         fontStyle: g.name ? undefined : "italic" }}>
                            {g.name || "No industry on record"}
                          </span>
                          <span style={{ fontSize:11, color:INK_MUTED, whiteSpace:"nowrap",
                                         fontVariantNumeric:"tabular-nums" }}>
                            {g.n} · {fmtRatio(share, 0)}
                          </span>
                        </div>
                        {/* A bar makes "one industry is most of this state"
                            visible without reading two numbers. */}
                        <div style={{ height:3, borderRadius:2, background:HAIRLINE,
                                      marginTop:2, overflow:"hidden" }}>
                          <div style={{ width: Math.max(share * 100, 1) + "%", height:"100%",
                                        background: B.cyan }} />
                        </div>
                      </div>
                    );
                  })}
                  {(shown.industryList || []).length > 4 && (
                    <div style={{ fontSize:11, color:INK_MUTED, marginTop:2 }}>
                      +{shown.industryList.length - 4} more
                    </div>
                  )}
                </div>

                <div style={{ marginTop:7, paddingTop:6, borderTop:"0.5px solid "+HAIRLINE,
                              color:INK_MUTED, lineHeight:1.5, fontSize:11,
                              minWidth:0, overflowWrap:"anywhere" }}>
                  {shown.accounts.slice(0, 4).map(a => a.name).join(", ")}
                  {shown.accounts.length > 4 ? ` +${shown.accounts.length - 4} more` : ""}
                </div>
              </>
            ) : (
              <div style={{ color:INK_MUTED, lineHeight:1.5 }}>
                Hover a state to name it here, with its accounts,
                industries, commitment and BS loan balance.
              </div>
            )}
          </div>

          {/* Borrowers with no usable state never make it onto the map. Saying
              so on the card is the difference between a map and a claim. */}
          {unassigned.n > 0 && (
            <div style={{ padding:"8px 10px", borderRadius:8, background:B.amberLight,
                          borderLeft:"3px solid "+B.amber, fontSize:11, lineHeight:1.5 }}>
              <strong>{unassigned.n} account{unassigned.n === 1 ? "" : "s"} not mapped</strong> —
              no state on the account record.{" "}
              {fmt(m.id === "commitment" ? unassigned.commitment : unassigned.bs)} of {m.label.toLowerCase()}
              {" "}sits outside this map.
              <div style={{ marginTop:4, color:INK_MUTED }}>
                {unassigned.names.join(", ")}
              </div>
            </div>
          )}
        </div>
      </div>

    </Card>
  );
};
